import find from 'lodash/find';
import includes from 'lodash/includes';
import filter from 'lodash/filter';
import orderBy from 'lodash/orderBy';
import map from 'lodash/map';
import type { NextRouter } from 'next/router';

import type { AppData } from '../../types/app_data';
import { AppStepSection, StepCategory } from '../../types/app_data';
import type { Step } from '../types/step';
import { RelationToUser } from '../../types/driver';
import { isValidOccupation, OCCUPATION_EXCLUSION_STATES } from '../../utils/occupations';
import { isEligibleForGeicoQuotes, isEligibleForMercuryQuotes } from '../../utils/onboard';
import type { SearchPolicyDriver } from '../../types/policy';
import { isRated } from '../../utils/driver';

const canAskOtherDriverOccupation = (appData: AppData, driver: SearchPolicyDriver) => {
  if (appData.stepSection === AppStepSection.Challenge) {
    if (driver?.industry && driver.occupation && isValidOccupation(driver.occupation)) {
      return false;
    }
  }

  return isEligibleForMercuryQuotes(appData);
};

const canAskSpouseOccupation = (appData: AppData) => {
  const spouseDriver = find(
    appData.drivers,
    (driver) => !driver.isArchived && driver.relationToUser === RelationToUser.Spouse
  );

  if (!appData.selectedService?.insurance) {
    return false;
  }

  if (appData.signupParams?.needNNOInsurance === true) {
    return false;
  }

  // If prefilled occupation for primary&spouse driver, skip the step
  if (
    appData.signupParams?.partnerSignup &&
    appData.partnerSkippableData?.hasOccupationSpouseDriverID === spouseDriver?.id
  ) {
    return false;
  }

  if (appData.stepSection === AppStepSection.Challenge) {
    // Skip the step is it's already answered
    if (spouseDriver?.industry && spouseDriver.occupation && isValidOccupation(spouseDriver.occupation)) {
      return false;
    }
  }

  return (
    !appData.isOnboardingLite &&
    !!spouseDriver &&
    (isEligibleForGeicoQuotes(appData) ? true : !includes(OCCUPATION_EXCLUSION_STATES, appData?.address?.state))
  );
};

const getDriverListForOccupation = (appData: AppData): Array<{ driver: SearchPolicyDriver; eligible: boolean }> => {
  const list = orderBy(
    map(appData.drivers, (driver) => {
      if (driver.isArchived || driver.relationToUser === RelationToUser.Insured) {
        return { driver, eligible: false };
      }

      if (!isRated(driver) && driver.relationToUser !== RelationToUser.Spouse) {
        return { driver, eligible: false };
      }

      if (canAskOtherDriverOccupation(appData, driver)) {
        return { driver, eligible: true };
      }

      if (driver.relationToUser === RelationToUser.Spouse) {
        return { driver, eligible: canAskSpouseOccupation(appData) };
      }

      return { driver, eligible: false };
    }),
    [(item) => item?.driver?.id],
    ['asc']
  );

  return list;
};

export const getCurrentOccupationDriver = (appData: AppData) => {
  const drivers = filter(getDriverListForOccupation(appData), (item) => item.eligible).map((item) => item.driver);
  return drivers.find((driver) => driver.id === appData.currentOccupationDriver?.id) || drivers[0];
};

export const getNextOccupationDriver = (appData: AppData, currentID: string) => {
  const list = getDriverListForOccupation(appData);
  const currentDriver = getCurrentOccupationDriver(appData);
  const currentIndex = list.findIndex((item) => item.driver.id === (currentID || currentDriver?.id));

  if (currentIndex < 0) {
    return;
  }

  return list.find((item, index) => index > currentIndex && item.eligible)?.driver;
};

// Ask drivers's occupation
export const OccupationDriverStep: Step = {
  id: 'occupationDriver',
  pathname: '/occupationDriver',
  canShow: (appData: AppData) => {
    const driver = getCurrentOccupationDriver(appData);
    return !!driver?.id;
  },
  hasData: () => true,
  hasOwnData: () => true,
  queryParams: (appData: AppData, router: NextRouter) => {
    return {
      occDrvId: getCurrentOccupationDriver(appData)?.id || router.query.occDrvId || null,
    };
  },
  category: () => StepCategory.Drivers,
  specialNextStep: {
    nextStepId: 'occupationDriver',
    enable: (appData: AppData) => {
      return !!appData?.currentOccupationDriver?.id;
    },
  },
};
